import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Paper,
  Stack,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Send as SubmitIcon,
  Close as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as SubmittedIcon,
  HourglassBottom as DraftIcon,
  Block as CancelledIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import {
  keystoneService,
  ImagingRequest,
  Modality,
} from '../../services/keystoneService';
import NewImagingRequestDialog from './NewImagingRequestDialog';
import ModalityBadge from './ModalityBadge';
import { groupByDay } from '../../utils/dateGrouping';

interface ImagingRequestsPanelProps {
  horseId: string;
  /**
   * When true, hide the panel's own "+ New imaging request" button. The
   * parent (LabsDiagnosticsPanel) owns a unified "+ New order" menu and
   * mounts the NewImagingRequestDialog itself, so we don't double up.
   */
  hideAddButton?: boolean;
  /**
   * When true, suppress the panel's own header (title + draft chip + add
   * button row). The parent renders an outer header that already conveys
   * counts and the order button, and a second sub-header inside the panel
   * looks redundant. Keeps the pending list flush with the parent layout.
   */
  hideHeader?: boolean;
  /**
   * Called when the user clicks Edit on a DRAFT row. Parent owns the dialog
   * (so Create + Edit share one mount) and is responsible for opening
   * NewImagingRequestDialog with `editTarget={req}`. Only DRAFT rows get
   * the edit affordance — SUBMITTED/CANCELLED rows are read-only.
   */
  onEdit?: (req: ImagingRequest) => void;
}

const STATUS_META: Record<
  ImagingRequest['submissionStatus'],
  { label: string; color: 'warning' | 'success' | 'default'; Icon: any }
> = {
  DRAFT: { label: 'Draft — not submitted', color: 'warning', Icon: DraftIcon },
  SUBMITTED: { label: 'Submitted to Keystone', color: 'success', Icon: SubmittedIcon },
  CANCELLED: { label: 'Cancelled', color: 'default', Icon: CancelledIcon },
};

/**
 * Panel for pre-Keystone imaging requests. AI-generated diagnostics and
 * manually-entered draft requests both land here in DRAFT status. Clinician
 * reviews and clicks Submit to actually push the MWL order to Keystone.
 */
const ImagingRequestsPanel: React.FC<ImagingRequestsPanelProps> = ({
  horseId,
  hideAddButton,
  hideHeader,
  onEdit,
}) => {
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [addOpen, setAddOpen] = useState(false);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [submitModalityId, setSubmitModalityId] = useState<number | ''>('');
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const { data: requests = [], isLoading } = useQuery<ImagingRequest[]>(
    ['imaging-requests', horseId],
    () => keystoneService.listImagingRequests(horseId),
    { enabled: !!horseId },
  );

  // Only a clinic with its own Keystone connection can submit; others keep the
  // draft as a reminder. Treated as connected while loading.
  const { data: keystoneStatus } = useQuery(
    ['keystone-status'],
    () => keystoneService.getStatus(),
    { staleTime: 60 * 1000 },
  );
  const isConnected = keystoneStatus === undefined || keystoneStatus.connected;

  const { data: modalities = [] } = useQuery<Modality[]>(
    ['keystone-modalities'],
    () => keystoneService.getModalities(),
    { staleTime: 5 * 60 * 1000, enabled: isConnected },
  );

  const submitMutation = useMutation(
    async ({ id, modalityId }: { id: string; modalityId?: number }) => {
      return keystoneService.submitImagingRequest(id, modalityId ? { modalityId } : undefined);
    },
    {
      onSuccess: () => {
        enqueueSnackbar('Submitted to Keystone', { variant: 'success' });
        qc.invalidateQueries(['imaging-requests', horseId]);
        setSubmittingId(null);
        setSubmitModalityId('');
      },
      onError: (e: any) => {
        enqueueSnackbar(e?.response?.data?.message || 'Submit failed', { variant: 'error' });
      },
    },
  );

  const cancelMutation = useMutation(
    async ({ id, reason }: { id: string; reason?: string }) =>
      keystoneService.cancelImagingRequest(id, reason),
    {
      onSuccess: () => {
        enqueueSnackbar('Request cancelled', { variant: 'info' });
        qc.invalidateQueries(['imaging-requests', horseId]);
        setCancellingId(null);
      },
      onError: (e: any) => {
        enqueueSnackbar(e?.response?.data?.message || 'Cancel failed', { variant: 'error' });
      },
    },
  );

  const deleteMutation = useMutation(
    async (id: string) => keystoneService.deleteImagingRequest(id),
    {
      onSuccess: () => {
        enqueueSnackbar('Draft deleted', { variant: 'info' });
        qc.invalidateQueries(['imaging-requests', horseId]);
      },
      onError: (e: any) => {
        enqueueSnackbar(e?.response?.data?.message || 'Delete failed', { variant: 'error' });
      },
    },
  );

  // Sort: DRAFT first (need attention), then SUBMITTED, then CANCELLED, each by date desc.
  const sorted = [...requests].sort((a, b) => {
    const order = { DRAFT: 0, SUBMITTED: 1, CANCELLED: 2 } as const;
    const d = order[a.submissionStatus] - order[b.submissionStatus];
    if (d !== 0) return d;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const draftCount = requests.filter(r => r.submissionStatus === 'DRAFT').length;

  const handleSubmitClick = (req: ImagingRequest) => {
    if (req.modalityId) {
      // Already has modality → one-click submit
      submitMutation.mutate({ id: req.id });
    } else {
      setSubmittingId(req.id);
      setSubmitModalityId('');
    }
  };

  return (
    <Box mb={hideHeader ? 1.5 : 3}>
      {!hideHeader && (
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle2" fontWeight={600}>
              Imaging Requests
            </Typography>
            {draftCount > 0 && (
              <Chip
                label={`${draftCount} draft${draftCount > 1 ? 's' : ''} awaiting submit`}
                size="small"
                color="warning"
                icon={<DraftIcon />}
              />
            )}
          </Box>
          {!hideAddButton && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setAddOpen(true)}
            >
              New imaging request
            </Button>
          )}
        </Box>
      )}

      {isLoading ? (
        <Box py={2} textAlign="center">
          <CircularProgress size={20} />
        </Box>
      ) : sorted.length === 0 ? (
        // Hide the dashed empty placeholder when embedded — the parent panel
        // already has its own empty state for the unified timeline.
        hideHeader ? null : (
          <Paper variant="outlined" sx={{ p: 2, borderStyle: 'dashed' }}>
            <Typography variant="body2" color="text.secondary">
              No imaging requests yet. AI-generated diagnostics from SOAP notes will land here as drafts.
            </Typography>
          </Paper>
        )
      ) : (
        <Stack spacing={1}>
          {/* CALENDAR, not instant: `requestedDateTime` is a naive clinic
            * wall-clock stored as if it were UTC (DATE-KINDS §2.5 W7 —
            * NewImagingRequestDialog serialises `YYYY-MM-DDTHH:mm:ss` with no
            * zone designator). Its date PART already IS the clinician's day, so
            * re-zoning it into any zone double-shifts it. */}
          {groupByDay(sorted, r => r.requestedDateTime, { kind: 'calendar' }).map(group => (
            <React.Fragment key={group.dayKey}>
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
                >
                  {group.dayLabel}
                </Typography>
                <Box sx={{ flex: 1, borderBottom: '1px dashed', borderColor: 'divider' }} />
                <Typography variant="caption" color="text.secondary">
                  {group.items.length} {group.items.length === 1 ? 'request' : 'requests'}
                </Typography>
              </Box>
              {group.items.map(req => {
                const meta = STATUS_META[req.submissionStatus];
                const MetaIcon = meta.Icon;
                const isDraft = req.submissionStatus === 'DRAFT';
                const isSubmitting = submitMutation.isLoading && submitMutation.variables?.id === req.id;
                return (
              <Paper key={req.id} variant="outlined" sx={{ p: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                  <ModalityBadge name={req.modalityName} size={32} />
                  <Box flex={1} minWidth={200}>
                    <Typography variant="body2" fontWeight={500}>
                      {req.modalityName || 'Imaging'} {req.bodyRegion ? `— ${req.bodyRegion}` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                      Acc #{req.accessionNumber}
                      {' · '}
                      {/* LEFT ON BASELINE: requestedDateTime is MIXED per row (dialog writes naive
                        * wall-clock digits, createDraftsFromSoapPlan writes a true instant) and
                        * soapNoteId is not a clean discriminator — waits for DATE-KINDS §2.5 W7. */}
                      {format(new Date(req.requestedDateTime), 'MMM d, yyyy h:mm a')}
                      {req.requestedBy ? ` · by ${req.requestedBy}` : ''}
                    </Typography>
                    {req.clinicalHistory && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        <em>History: {req.clinicalHistory}</em>
                      </Typography>
                    )}
                  </Box>
                  <Chip icon={<MetaIcon fontSize="small" />} label={meta.label} color={meta.color} size="small" />

                  {isDraft && (
                    <>
                      {onEdit && (
                        <Tooltip title="Edit this draft">
                          <IconButton size="small" onClick={() => onEdit(req)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isConnected ? (
                        <Tooltip title={req.modalityId ? 'Submit to Keystone' : 'Choose modality then submit'}>
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : <SubmitIcon />}
                              disabled={submitMutation.isLoading}
                              onClick={() => handleSubmitClick(req)}
                            >
                              Submit
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Connect Keystone in Settings > Integrations to submit imaging orders">
                          <Typography variant="caption" color="text.secondary">
                            Imaging not connected
                          </Typography>
                        </Tooltip>
                      )}
                      <Tooltip title="Cancel this draft">
                        <IconButton
                          size="small"
                          onClick={() => cancelMutation.mutate({ id: req.id })}
                          disabled={cancelMutation.isLoading}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete draft">
                        <IconButton
                          size="small"
                          onClick={() => deleteMutation.mutate(req.id)}
                          disabled={deleteMutation.isLoading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Paper>
                );
              })}
            </React.Fragment>
          ))}
        </Stack>
      )}

      {/* Submit dialog — opens when DRAFT has no modalityId and user clicks Submit */}
      <Dialog open={!!submittingId} onClose={() => setSubmittingId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Select Modality</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Pick the Keystone modality for this study before submitting.
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            label="Modality"
            value={submitModalityId}
            onChange={e => setSubmitModalityId(Number(e.target.value))}
          >
            {modalities.map(m => (
              <MenuItem key={m.modalityId} value={m.modalityId}>
                {m.modalityName}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmittingId(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!submitModalityId || submitMutation.isLoading}
            onClick={() =>
              submittingId &&
              submitMutation.mutate({ id: submittingId, modalityId: Number(submitModalityId) })
            }
            startIcon={submitMutation.isLoading ? <CircularProgress size={14} /> : <SubmitIcon />}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* New request dialog — only mounted when this panel owns its add
          button. When parent uses hideAddButton, parent owns the dialog. */}
      {!hideAddButton && (
        <NewImagingRequestDialog
          open={addOpen}
          horseId={horseId}
          onClose={() => setAddOpen(false)}
          onSuccess={() => {
            setAddOpen(false);
            qc.invalidateQueries(['imaging-requests', horseId]);
          }}
        />
      )}
      {!hideHeader && <Divider sx={{ mt: 2 }} />}
    </Box>
  );
};

export default ImagingRequestsPanel;
